import { useController } from "react-hook-form";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  RadioGroup,
  Radio,
  VStack,
  Text,
  Box,
} from "@chakra-ui/react";

/**
 * Whole-trip vs any-night alerts for a multi-night route. Each option spells
 * out its consequence with the user's own route, because most trip-planner
 * support threads were people surprised by single-night alerts they had
 * opted into without seeing what that meant.
 *
 * Props:
 *   nightLabels — one short label per night of the current route
 */
export function PartialMatchToggle({ formContext, nightLabels = [], ...rest }) {
  const { control } = formContext;

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name: "allowPartialMatch",
    rules: {
      validate: (v) =>
        v === true || v === false || "Please select one of the options above",
    },
  });

  const radioValue = value === true ? "yes" : value === false ? "no" : "";

  const handleChange = (val) => {
    onChange(val === "yes");
  };

  const nightCount = nightLabels.length;
  // A middle night makes the clearest example of a night on its own. The
  // example names one site, so drop the "+N" count of alternatives.
  const exampleNight = (
    nightLabels[Math.min(1, nightCount - 1)] || nightLabels.find(Boolean)
  )?.replace(/ \+\d+$/, "");

  return (
    <FormControl as="fieldset" isInvalid={!!error} isRequired {...rest}>
      <FormLabel as="legend">
        Which openings should we alert you about?
      </FormLabel>
      <RadioGroup value={radioValue} onChange={handleChange}>
        <VStack spacing={3} align="stretch">
          <Box>
            <Radio value="no" colorScheme="brand">
              <Text>Only the whole trip</Text>
            </Radio>
            <Text fontSize="sm" color="subtle" ml={6} mt={0.5}>
              All {nightCount} nights open from the same start day. Fewer
              alerts, and you can book the trip in one go.
            </Text>
          </Box>
          <Box>
            <Radio value="yes" colorScheme="brand">
              <Text>Any single night</Text>
            </Radio>
            <Text fontSize="sm" color="subtle" ml={6} mt={0.5}>
              {exampleNight
                ? `For example, just ${exampleNight} for one night. `
                : ""}
              More alerts, but you'd piece the trip together night by night.
            </Text>
          </Box>
        </VStack>
      </RadioGroup>
      {error && <FormErrorMessage>{error.message}</FormErrorMessage>}
    </FormControl>
  );
}
